import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Download, Trash2, FileText, Image, File } from 'lucide-react';
import { TripDocument } from '../../api/documents';

interface DocumentCardProps {
  document: TripDocument;
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
  canDelete?: boolean;
  isDeleting?: boolean;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onPreview,
  onDownload,
  onDelete,
  canDelete = false,
  isDeleting = false,
}) => {
  const { t } = useTranslation();

  const isImage = document.mimeType.startsWith('image/');
  const isPDF = document.mimeType === 'application/pdf';

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getIcon = () => {
    if (isImage) return <Image className="w-5 h-5 text-blue-500" />;
    if (isPDF) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="flex items-center p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
      <div className="flex-shrink-0 mr-3">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate" title={document.fileName}>
          {document.fileName}
        </p>
        <p className="text-xs text-gray-500">
          {formatFileSize(document.fileSize)} •{' '}
          {new Date(document.uploadedAt).toLocaleDateString('vi-VN')}
        </p>
      </div>

      <div className="flex items-center space-x-1 ml-2">
        {(isImage || isPDF) && (
          <button
            type="button"
            onClick={onPreview}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
            title={t('documents.preview')}
          >
            <Eye className="w-4 h-4" />
          </button>
        )}

        <button
          type="button"
          onClick={onDownload}
          className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
          title={t('documents.download')}
        >
          <Download className="w-4 h-4" />
        </button>

        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
            title={t('documents.delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
